import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import Slider from 'react-slick';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import AOS from 'aos';
import {
  FaArrowRight, FaRocket, FaCheckCircle, FaCode, FaGraduationCap,
  FaBullhorn, FaUsers, FaRobot, FaCloud, FaShieldAlt, FaGlobe,
  FaBrain, FaHeadset, FaStar, FaPlay, FaChevronRight,
  FaBuilding, FaHospital, FaShoppingCart, FaTruck, FaChartLine,
  FaQuoteLeft, FaNetworkWired
} from 'react-icons/fa';
import divisionsData from '../data/divisions.json';
import productsData from '../data/products.json';
import portfolioData from '../data/portfolio.json';
import featuresData from '../data/features.json';
import './Home.css';

const ICON_MAP = {
  FaCode: <FaCode />, FaGraduationCap: <FaGraduationCap />,
  FaBullhorn: <FaBullhorn />, FaUsers: <FaUsers />, FaRobot: <FaRobot />,
  FaBuilding: <FaBuilding />, FaShoppingCart: <FaShoppingCart />,
  FaTruck: <FaTruck />, FaCloud: <FaCloud />, FaBrain: <FaBrain />,
  FaShieldAlt: <FaShieldAlt />, FaGlobe: <FaGlobe />,
  FaRocket: <FaRocket />, FaHeadset: <FaHeadset />,
  FaChartLine: <FaChartLine />, FaNetworkWired: <FaNetworkWired />,
};

const SLIDES = [
  {
    badge: 'Enterprise Software Solutions',
    title: 'Empowering Businesses with',
    subtitle: 'Scalable Digital Solutions',
    desc: 'Vkomak Technologies delivers enterprise-grade software products and digital transformation services across industries. From ERP to AI-powered platforms, we build solutions that drive growth, efficiency, and innovation.',
    class: 'slide-1',
  },
  {
    badge: 'HRMS · LMS · ERP · CRM',
    title: 'Enterprise Products',
    subtitle: 'Built for Real Business',
    desc: 'From HR management and learning systems to full ERP suites and CRM platforms — our products are production-ready, scalable, and built with .NET Core, Angular, React, and PostgreSQL.',
    class: 'slide-2',
  },
  {
    badge: 'Digital Transformation Partner',
    title: 'From Idea to',
    subtitle: 'Enterprise Product',
    desc: 'We partner with startups, SMEs, and global enterprises to transform ideas into world-class digital products. End-to-end product engineering with speed and precision.',
    class: 'slide-3',
  },
  {
    badge: 'AI-Ready Architecture',
    title: 'Intelligent Software',
    subtitle: 'for the Modern Enterprise',
    desc: 'AI integration, cloud-native architecture, and scalable SaaS platforms — Vkomak builds the digital backbone your business needs to thrive in the age of automation.',
    class: 'slide-4',
  },
];

const STATS = [
  { value: 5, suffix: '+', label: 'Live Global Clients', icon: <FaUsers /> },
  { value: 30, suffix: '+', label: 'Training & Dev Projects', icon: <FaGraduationCap /> },
  { value: 5, suffix: '+', label: 'Years Experience', icon: <FaChartLine /> },
  { value: 100, suffix: '+', label: 'Projects Delivered', icon: <FaCode /> },
];

const INDUSTRIES = [
  { name: 'Education', icon: <FaGraduationCap />, color: '#0891b2' },
  { name: 'Retail & eCommerce', icon: <FaShoppingCart />, color: '#d97706' },
  { name: 'Healthcare', icon: <FaHospital />, color: '#dc2626' },
  { name: 'Logistics', icon: <FaTruck />, color: '#059669' },
  { name: 'Finance & FinTech', icon: <FaChartLine />, color: '#7c3aed' },
  { name: 'Enterprise & IT', icon: <FaBuilding />, color: '#1e40af' },
];

const AnimatedCounter = ({ value, suffix, label, icon }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
  return (
    <div ref={ref} className="stat-item">
      <div className="stat-icon-wrap">{icon}</div>
      <div className="stat-number">
        {inView ? <CountUp end={value} duration={2.5} /> : '0'}
        <span className="stat-suffix">{suffix}</span>
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
};

const Home = () => {
  useEffect(() => {
    AOS.init({ duration: 900, once: true, offset: 80 });
  }, []);

  const heroSettings = {
    dots: true, infinite: true, speed: 800, slidesToShow: 1,
    slidesToScroll: 1, autoplay: true, autoplaySpeed: 5500,
    fade: true, arrows: false, pauseOnHover: false,
  };



  return (
    <div className="home-page">

      {/* ── HERO SLIDER ── */}
      <section className="hero-section">
        <Slider {...heroSettings}>
          {SLIDES.map((slide, i) => (
            <div key={i} className={`hero-slide ${slide.class}`}>
              <div className="hero-overlay" />
              <Container>
                <Row className="align-items-center hero-row">
                  <Col lg={7}>
                    <div className="hero-content">
                      <div className="hero-badge">{slide.badge}</div>
                      <h1 className="hero-title">
                        {slide.title}<br />
                        <span className="hero-gradient">{slide.subtitle}</span>
                      </h1>
                      <p className="hero-desc">{slide.desc}</p>
                      <div className="hero-actions">
                        <Link to="/contact" className="btn-primary-vk">
                          Get Started <FaArrowRight />
                        </Link>
                        <Link to="/contact" className="btn-outline-vk">
                          Request Demo <FaPlay />
                        </Link>
                      </div>
                      <div className="hero-trust">
                        <FaCheckCircle className="trust-icon" />
                        Trusted by enterprises across India &amp; globally
                      </div>
                    </div>
                  </Col>
                  <Col lg={5} className="d-none d-lg-block">
                    <div className="hero-visual">
                      <div className="hero-orb hero-orb-1" />
                      <div className="hero-orb hero-orb-2" />
                      <div className="hero-orb hero-orb-3" />
                      <div className="hero-globe-ring">
                        <div className="hero-center-icon"><FaRocket /></div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Container>
            </div>
          ))}
        </Slider>
      </section>

      {/* ── STATS ── */}
      <section className="stats-section">
        <Container>
          <div className="stats-grid">
            {STATS.map((s, i) => <AnimatedCounter key={i} {...s} />)}
          </div>
        </Container>
      </section>

      {/* ── ABOUT SNAPSHOT ── */}
      <section className="about-snap section-py">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6} data-aos="fade-right">
              <span className="section-badge">About Vkomak</span>
              <h2 className="section-title">
                We Don't Just Build Software —<br />
                <span>We Build Digital Ecosystems</span>
              </h2>
              <div className="gradient-line" />
              <p className="section-subtitle mb-4">
                Vkomak Technologies is a <strong>SaaS and service-based technology company</strong> focused on delivering scalable, secure, and intelligent business solutions.
                With expertise in enterprise systems, AI integration, and modern web technologies, we help startups, SMEs, and global enterprises transform digitally.
              </p>
              <ul className="about-checklist">
                {[
                  'Enterprise Software Development (.NET Core, Angular, React)',
                  'AI & Data-Driven Solutions with OpenAI integration',
                  'Cloud & Scalable Architecture on AWS and Azure',
                  'End-to-End Product Engineering — idea to launch',
                ].map((item, i) => (
                  <li key={i} className="check-item">
                    <FaCheckCircle className="check-icon" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/about" className="btn-secondary-vk mt-4">
                Learn More About Us <FaArrowRight />
              </Link>
            </Col>
            <Col lg={6} data-aos="fade-left">
              <div className="about-card-grid">
                {[
                  { icon: <FaRobot />, label: 'AI Products', sub: '8+ Live', color: '#7c3aed' },
                  { icon: <FaCode />, label: 'Custom Dev', sub: '100+ Projects', color: '#1e40af' },
                  { icon: <FaGraduationCap />, label: 'TalentX', sub: '33+ Placed', color: '#0891b2' },
                  { icon: <FaBullhorn />, label: 'PromoX', sub: 'Growth Marketing', color: '#059669' },
                ].map((card, i) => (
                  <div key={i} className="about-mini-card" data-aos="zoom-in" data-aos-delay={i * 100}>
                    <div className="about-mini-icon" style={{ background: `${card.color}15`, color: card.color }}>
                      {card.icon}
                    </div>
                    <div>
                      <div className="about-mini-label">{card.label}</div>
                      <div className="about-mini-sub">{card.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="about-quote-banner">
                <FaRocket />
                <span>"We build scalable digital products &amp; business ecosystems"</span>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ── DIVISIONS ── */}
      <section className="divisions-section section-py">
        <Container>
          <div className="section-header text-center" data-aos="fade-up">
            <span className="section-badge">Our Divisions</span>
            <h2 className="section-title">
              Four Pillars of the <span>Vkomak Group</span>
            </h2>
            <p className="section-subtitle">
              A diversified technology group delivering excellence across software products, talent, marketing, and staffing.
            </p>
          </div>
          <Row className="g-4">
            {divisionsData.map((div, i) => (
              <Col lg={3} md={6} key={div.id} data-aos="fade-up" data-aos-delay={i * 100}>
                <div className="division-card">
                  <div className="division-top" style={{ background: `linear-gradient(135deg, ${div.color}18, ${div.color}06)` }}>
                    <div className="division-icon-wrap" style={{ background: `${div.color}20`, color: div.color }}>
                      {ICON_MAP[div.icon]}
                    </div>
                    <div className="division-badge" style={{ background: div.color }}>Division</div>
                  </div>
                  <div className="division-body">
                    <h3 className="division-title">{div.name}</h3>
                    <p className="division-tagline" style={{ color: div.color }}>{div.tagline}</p>
                    <p className="division-desc">{div.description}</p>
                    <ul className="division-features">
                      {div.features.map((f, fi) => (
                        <li key={fi}><FaCheckCircle style={{ color: div.color }} /> {f}</li>
                      ))}
                    </ul>
                    <Link to={div.link} className="division-link" style={{ '--div-color': div.color }}>
                      Learn More <FaArrowRight />
                    </Link>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ── PRODUCTS ── */}
      <section className="products-section section-py">
        <Container>
          <div className="section-header text-center" data-aos="fade-up">
            <span className="section-badge">Our Products</span>
            <h2 className="section-title">
              <span>Enterprise SaaS</span> Products
            </h2>
            <p className="section-subtitle">
              A complete ecosystem of AI-powered SaaS tools built for every business function.
            </p>
          </div>
          <div className="products-grid" data-aos="fade-up">
            {productsData.map((product) => (
              <div key={product.id} className="product-card">
                <div className="product-icon-wrap" style={{ background: `${product.badgeColor}12`, color: product.badgeColor }}>
                  {ICON_MAP[product.icon]}
                </div>
                <span className="product-badge" style={{ background: product.badgeColor }}>{product.badge}</span>
                <h4 className="product-name">{product.name}</h4>
                <p className="product-tagline">{product.tagline}</p>
                <ul className="product-features-list">
                  {product.features.map((f, fi) => (
                    <li key={fi}><FaChevronRight style={{ color: product.badgeColor }} /> {f}</li>
                  ))}
                </ul>
                <span className="product-status-live">● {product.status}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-5" data-aos="fade-up">
            <Link to="/products" className="btn-primary-vk">
              Explore All Products <FaArrowRight />
            </Link>
          </div>
        </Container>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="why-section section-py">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={4} data-aos="fade-right">
              <span className="section-badge">Why Choose Us</span>
              <h2 className="section-title">
                Built for the <span>Future of Business</span>
              </h2>
              <div className="gradient-line" />
              <p className="section-subtitle mb-4">
                We combine deep technical expertise with business acumen to deliver solutions that are not just functional — but transformational.
              </p>
              <Link to="/about" className="btn-primary-vk">
                Our Story <FaArrowRight />
              </Link>
            </Col>
            <Col lg={8} data-aos="fade-left">
              <Row className="g-3">
                {featuresData.map((feat, i) => (
                  <Col md={6} key={feat.id} data-aos="zoom-in" data-aos-delay={i * 80}>
                    <div className="why-card">
                      <div className="why-icon" style={{ background: `${feat.color}12`, color: feat.color }}>
                        {ICON_MAP[feat.icon]}
                      </div>
                      <h5 className="why-title">{feat.title}</h5>
                      <p className="why-desc">{feat.description}</p>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ── INDUSTRIES ── */}
      <section className="industries-section section-py">
        <Container>
          <div className="section-header text-center" data-aos="fade-up">
            <span className="section-badge">Industries We Serve</span>
            <h2 className="section-title">
              Powering Growth Across <span>Every Sector</span>
            </h2>
          </div>
          <div className="industries-grid">
            {INDUSTRIES.map((ind, i) => (
              <div key={i} className="industry-card" data-aos="zoom-in" data-aos-delay={i * 70}>
                <div className="industry-icon" style={{ background: `${ind.color}12`, color: ind.color }}>
                  {ind.icon}
                </div>
                <span className="industry-name">{ind.name}</span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── PORTFOLIO HIGHLIGHTS ── */}
      <section className="portfolio-highlights-section section-py">
        <Container>
          <div className="section-header text-center" data-aos="fade-up">
            <span className="section-badge">Our Portfolio</span>
            <h2 className="section-title">
              Enterprise <span>Product Portfolio</span>
            </h2>
            <p className="section-subtitle">
              From HRMS and LMS to ERP, CRM, and E-Commerce — purpose-built products solving real business challenges.
            </p>
          </div>
          <Row className="g-4 mt-1">
            {portfolioData.slice(0, 3).map((project, i) => (
              <Col lg={4} md={6} key={project.id} data-aos="fade-up" data-aos-delay={i * 100}>
                <div className="pf-hl-card" style={{ '--pf-accent': project.color }}>
                  <div className="pf-hl-top" style={{ background: `linear-gradient(135deg, ${project.color}15, ${project.color}05)` }}>
                    <div className="pf-hl-icon" style={{ background: `${project.color}18`, color: project.color }}>
                      {ICON_MAP[project.icon]}
                    </div>
                    <span className="pf-hl-badge" style={{ background: project.color }}>{project.badge}</span>
                  </div>
                  <div className="pf-hl-body">
                    <h4 className="pf-hl-title">{project.title}</h4>
                    <p className="pf-hl-tagline" style={{ color: project.color }}>{project.tagline}</p>
                    <p className="pf-hl-desc">{project.description.substring(0, 100)}...</p>
                    <div className="pf-hl-techs">
                      {project.technologies.slice(0, 3).map((t, ti) => (
                        <span key={ti} className="pf-hl-tech">{t}</span>
                      ))}
                    </div>
                    <Link to={`/portfolio/${project.id}`} className="pf-hl-link" style={{ color: project.color }}>
                      View Details <FaArrowRight />
                    </Link>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          <div className="text-center mt-5" data-aos="fade-up">
            <Link to="/portfolio" className="btn-primary-vk">
              View Full Portfolio <FaArrowRight />
            </Link>
          </div>
        </Container>
      </section>

      {/* ── TECH STACK ── */}
      <section className="techstack-section section-py">
        <Container>
          <div className="section-header text-center" data-aos="fade-up">
            <span className="section-badge">Technologies We Use</span>
            <h2 className="section-title">
              Our <span>Technology Stack</span>
            </h2>
            <p className="section-subtitle">Enterprise-grade technologies powering every product we deliver.</p>
          </div>
          <div className="techstack-grid" data-aos="fade-up" data-aos-delay="100">
            {[
              { layer: 'Frontend', items: ['Angular', 'React', 'HTML5 / CSS3', 'Bootstrap 5', 'TypeScript'], color: '#1e40af' },
              { layer: 'Backend', items: ['.NET Core', 'C# / Python', 'REST APIs', 'GraphQL', 'Microservices'], color: '#7c3aed' },
              { layer: 'Database', items: ['SQL Server', 'PostgreSQL', 'Redis', 'MongoDB'], color: '#0891b2' },
              { layer: 'Cloud & AI', items: ['AWS', 'Azure', 'Docker / K8s', 'OpenAI APIs', 'CI/CD'], color: '#059669' },
            ].map((stack, i) => (
              <div key={i} className="techstack-card" data-aos="zoom-in" data-aos-delay={i * 80}>
                <div className="techstack-layer" style={{ color: stack.color, background: `${stack.color}0f` }}>
                  {stack.layer}
                </div>
                <div className="techstack-items">
                  {stack.items.map((item, ii) => (
                    <span key={ii} className="techstack-item" style={{ borderColor: `${stack.color}35`, color: stack.color, background: `${stack.color}08` }}>
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── TRUSTED BY LEADING ORGANIZATIONS ── */}
      <section className="trusted-by-section section-py">
        <Container>
          <div className="section-header text-center" data-aos="fade-up">
            <h2 className="section-title">Trusted By <span>Leading Organizations</span></h2>
            <p className="section-subtitle">Our growing network of enterprise partners and clients across industries.</p>
          </div>
          <div className="trusted-logo-grid" data-aos="fade-up" data-aos-delay="100">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="trusted-logo-slot">
                <div className="trusted-logo-inner">
                  <div className="trusted-shimmer" />
                </div>
              </div>
            ))}
          </div>
          <div className="text-center" style={{ marginTop: '2rem' }} data-aos="fade-up" data-aos-delay="200">
            <span className="coming-pill">
              <span className="pulse-dot" />
              Partner Logos Coming Soon
            </span>
          </div>
        </Container>
      </section>

      {/* ── TESTIMONIALS COMING SOON ── */}
      <section className="testimonials-soon section-py">
        <Container>
          <div className="section-header text-center" data-aos="fade-up">
            <span className="section-badge">Testimonials</span>
            <h2 className="section-title">What Our <span>Clients Say</span></h2>
            <p className="section-subtitle">Success stories from enterprises and businesses we've transformed with technology.</p>
          </div>
          <div className="testi-soon-wrap" data-aos="fade-up" data-aos-delay="100">
            <div className="testi-soon-cards">
              {[0, 1, 2].map(i => (
                <div key={i} className="testi-soon-card">
                  <div className="testi-soon-stars">
                    {[...Array(5)].map((_, si) => <FaStar key={si} />)}
                  </div>
                  <div className="testi-soon-lines">
                    <div className="testi-line l1" />
                    <div className="testi-line l2" />
                    <div className="testi-line l3" />
                  </div>
                  <div className="testi-soon-foot">
                    <div className="testi-soon-av" />
                    <div>
                      <div className="testi-line la" />
                      <div className="testi-line lb" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="testi-soon-overlay">
              <FaQuoteLeft className="testi-soon-icon" />
              <h3 className="testi-soon-title">Client Success Stories</h3>
              <p className="testi-soon-desc">
                We are collecting and verifying testimonials from our enterprise clients.<br />
                Real reviews and case studies will be featured here shortly.
              </p>
              <div className="coming-pill">
                <span className="pulse-dot" />
                Coming Soon
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ── CTA ── */}
      <section className="cta-section">
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8} data-aos="zoom-in">
              <h2 className="cta-title">
                Let's Build Your Next<br />
                <span>Digital Product</span>
              </h2>
              <p className="cta-desc">
                Transform your vision into a scalable, AI-powered product that grows with your business.
              </p>
              <div className="cta-actions">
                <Link to="/contact" className="btn-primary-vk">
                  Start Your Project <FaArrowRight />
                </Link>
                <Link to="/contact" className="btn-outline-vk">
                  Request Demo <FaPlay />
                </Link>
              </div>
              <div className="cta-trust-row">
                <span><FaCheckCircle /> No commitment required</span>
                <span><FaCheckCircle /> Free consultation</span>
                <span><FaCheckCircle /> 24/7 support</span>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

    </div>
  );
};

export default Home;
